import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { IconDanger, IconSuccess } from '../../../core';
import commonPageContentStyles from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Government accessibility resources',
    cards: [
      {
        title: 'The Section 508 accessibility program',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508Program,
      },
      {
        title: 'Accessibility for Teams guide',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovAccessibilityForTeams,
      },
      {
        title: 'Find your Section 508 Coordinator',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508ProgramManagerListing,
      },
      {
        title: 'Section 508 ICT Testing Baseline',
        subtitle: '[ictbaseline.access-board.gov]',
        linkUrl: REFERENCE_LINKS.ictTestingBaseline,
      },
      {
        title: 'Learn about writing in plain language ',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovPlainLanguageGuide,
      },
    ],
  },
];

const CommunicationStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Communication"
        breadcrumbs={[
          { label: 'General Components', url: '/general-components' },
          { label: 'Communication', url: '/' },
        ]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <h3 className="h3" id="definition">
                  Definition
                </h3>
                <p className="mb-40">
                  In design systems, the Communication section defines how visual and written
                  elements, such as badges, tooltips, and chart tooltips, work together to provide
                  clear, concise messaging. These communication elements serve as essential
                  indicators, offering contextual information and supplementary details that enhance
                  user understanding and interaction. By delivering pertinent information clearly
                  and intuitively, they streamline the user experience, contributing to an effective
                  and cohesive design system while ensuring alignment with the brand’s identity.
                </p>
                <h3 className="h3" id="accessibility">
                  Accessibility
                </h3>
                <p className="font-sm mb-40">
                  In design systems, accessibility ensures that all users, including those with
                  disabilities, can interact with and understand the interface. Communication
                  elements like badges, tooltips, and chart tooltips must be designed with
                  accessibility in mind, ensuring they are perceivable, operable, and
                  understandable. This includes providing alternative text for screen readers,
                  ensuring sufficient contrast for readability, and making interactive elements
                  keyboard-navigable. By prioritizing accessibility, design systems can create
                  inclusive, user-friendly experiences that cater to a wider audience while
                  maintaining functionality and clarity.
                </p>
              </div>
              <h2 className="h1 divider" id="specifications">
                Specifications
              </h2>
              <div className="page-content-item">
                <h3 className="h3" id="tag-badge">
                  Tag &amp; Badge
                </h3>
                <p className="mb-40">
                  Tags and badges are compact elements used to convey important information or
                  categorize content within a UI. Tags typically categorize or label items, while
                  badges highlight numerical or status information. Both elements are used to draw
                  attention to specific content, providing clarity and context for the user. Tags
                  often represent a category or topic, such as &quot;New&quot; or &quot;Sale,&quot;
                  while badges display data, like unread notifications or task counts. It&apos;s
                  essential to design these elements to be visually distinct and easily understood
                  to enhance the user experience, ensuring they serve their purpose without
                  overwhelming the interface.
                </p>
              </div>
              <div className="number-cards mb-40">
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">1</span>
                    <img
                      src="/images/general-components-communication/screen-tag-badge-01@2x.webp"
                      width={488}
                      height={264}
                      alt="Heading"
                    />
                  </div>
                  <div className="number-card-desc">
                    <p className="text-paragraph-medium">1. Tag 2. Badge</p>
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">2</span>
                    <img
                      src="/images/general-components-communication/screen-tag-badge-02@2x.webp"
                      width={488}
                      height={264}
                      alt="Body"
                    />
                  </div>
                </div>
              </div>
              <h3 className="h3 divider" id="tooltip">
                Tooltip
              </h3>
              <div className="page-content-item">
                <p className="mb-40">
                  Tooltips are small, informative pop-ups that appear when a user hovers over,
                  focuses on, or taps a UI element. They provide additional information or
                  clarification about the element, offering context without cluttering the
                  interface. Tooltips are typically brief and concise, helping users understand the
                  function of a button, link, or other interactive elements. Effective tooltips are
                  accessible, clear, and well-timed, appearing when needed and disappearing when no
                  longer relevant. Proper design ensures they don&apos;t obscure essential content
                  and are easy to read, contributing to a more intuitive and user-friendly
                  experience.
                </p>
              </div>
              <img
                src="/images/general-components-communication/screen-tooltip@2x.webp"
                width={1000}
                height={558}
                alt=""
                className="mb-40 img-responsive"
              />
              <h2 className="h1 divider" id="guidelines">
                Guidelines
              </h2>
              <div className="page-content-item">
                <div className="visually-hidden" aria-hidden="true" id="usage">
                  Usage
                </div>
                <h3 className="h3" id="tag-badge-usage">
                  Tag &amp; Badge Usage
                </h3>
                <p className="mb-40">
                  Tags and badges are used to provide additional context or categorize content,
                  helping users quickly identify key attributes or statuses. Tags are typically used
                  to categorize or label items with descriptive keywords, while badges represent
                  counts, statuses, or notifications. When using tags and badges, it’s essential to
                  ensure they are placed in a way that enhances the clarity of the interface without
                  overcrowding the content. Use tags for categorization and filters, and badges to
                  communicate small, relevant data points like unread messages, new items, or
                  progress statuses. Keep them concise, consistent, and easy to understand for
                  effective communication.
                </p>
              </div>
              <table className="table table-variants text-h5 mb-40">
                <thead>
                  <tr>
                    <th style={{ width: '15.625rem' }}>Component</th>
                    <th>Use case</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td valign="top">Badge</td>
                    <td>
                      The badge component indicates a status or count. Badges are read-only status
                      indicators or labels and are not interactive.
                    </td>
                  </tr>
                  <tr>
                    <td valign="top">Tag</td>
                    <td>
                      Use tags to allow users to group, sort, or filter information. Tags frequently
                      contain keywords or metadata. They provide quick access to specific categories
                      and support content classification and navigation.
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="page-content-item">
                <h3 className="h3" id="tooltip-usage">
                  Tooltip Usage
                </h3>
                <p className="font-sm mb-40">
                  Tooltips are used to provide additional information or context when users hover
                  over or focus on a specific element. They should be concise and offer valuable
                  insights without overwhelming the user. Tooltips are most effective for enhancing
                  user understanding of icons, buttons, or form fields where the functionality may
                  not be immediately clear. Ensure that tooltips are placed near the element they
                  describe and appear only when necessary, avoiding clutter in the interface. Be
                  mindful of accessibility, ensuring tooltips are readable by screen readers and
                  that users can easily dismiss them when no longer needed.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-communication/screen-tooltip-usage-01@2x.webp"
                    width={488}
                    height={349}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p className="text-paragraph-large">
                      Use consistent language for the button and other text descibing the same
                      action.
                    </p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-communication/screen-tooltip-usage-02@2x.webp"
                    width={488}
                    height={349}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p className="text-paragraph-large">
                      Use different words to refer to the same action.
                    </p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <h3 className="h3" id="tooltip-in-data-visualization">
                  Tooltip in Data Visualization
                </h3>
                <p className="mb-8">
                  In data visualization, tooltips offer additional details about data points when
                  users hover over elements like bars or dots. They provide context, values, or
                  trends, enhancing data interpretation without cluttering the interface. Ensure
                  tooltips are concise, clear, and accessible, supporting screen readers for users
                  with disabilities.
                </p>
                <p className="mb-40">
                  Use the large tooltip when additional context or detailed information is
                  necessary, such as explaining complex data or offering extended descriptions. This
                  size is ideal for more intricate data visualizations or when providing
                  supplementary guidance without overwhelming the user. Ensure the content is easy
                  to read and focused, maintaining a clear, intuitive layout.
                </p>
              </div>
              <figure className="figure-sm">
                <img
                  src="/images/general-components-communication/screen-tooltip-in-data-visualization-01@2x.webp"
                  width={1000}
                  height={487}
                  alt=""
                  className="img-responsive"
                />
                <figcaption>
                  Use the Small Tooltip when there is one data set. Since only one set of data is
                  being shown, the label and color border are not necessary.
                </figcaption>
              </figure>
              <figure className="figure-sm">
                <img
                  src="/images/general-components-communication/screen-tooltip-in-data-visualization-02@2x.webp"
                  width={1000}
                  height={487}
                  alt=""
                  className="img-responsive"
                />
                <figcaption>
                  The Medium Tooltip is used in scenarios where two or more sets of data are being
                  compared. The top border color on the Tooltip should match the color of the data
                  it is representing, as shown in the key. The label can either be referring to the
                  corresponding name in the key or the intersecting data increment on the x-axis.
                </figcaption>
              </figure>
              <div className="page-content-item">
                <h3 className="h3" id="placement">
                  Placement
                </h3>
                <p className="mb-40">
                  For placement, position tooltips near the target element they are describing,
                  ensuring they don’t obstruct critical content. Tooltips should be placed above,
                  below, or to the side of the element, depending on available space. Ensure that
                  tooltips are positioned dynamically to avoid crowding the viewport, and allow
                  users to easily see the relevant information without any overlap with other UI
                  elements.
                </p>
              </div>
              <div className="number-cards row-gap-24 mb-24">
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">1</span>
                    <img
                      src="/images/general-components-communication/screen-placement-01@2x.webp"
                      width={488}
                      height={248}
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">2</span>
                    <img
                      src="/images/general-components-communication/screen-placement-02@2x.webp"
                      width={488}
                      height={248}
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">3</span>
                    <img
                      src="/images/general-components-communication/screen-placement-03@2x.webp"
                      width={488}
                      height={248}
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">4</span>
                    <img
                      src="/images/general-components-communication/screen-placement-04@2x.webp"
                      width={488}
                      height={248}
                      alt=""
                    />
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <p className="mb-40">
                  Tooltip containers can be automatically or manually positioned to the right, left,
                  bottom, or top
                </p>
              </div>
              <h2 className="h1 divider" id="references">
                References
              </h2>
              {dataExternalLinkGroups.map((externalLinkGroup) => (
                <ExternalLinkGroupStyled
                  key={externalLinkGroup.id}
                  title={externalLinkGroup.title}
                  cards={externalLinkGroup.cards}
                />
              ))}
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'definition',
                    'accessibility',
                    'specifications',
                    'tag-badge',
                    'tooltip',
                    'guidelines',
                    'usage',
                    'placement',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/general-components/form-elements"
        previous="Form Elements"
        nextLink="/general-components/navigation"
        next="Navigation"
      />
    </div>
  </div>
);

const Communication = styled(CommunicationStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 3, 5, 6, 8, 9])}

    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }
`;

export default Communication;
